/**
 * SpellList component for managing attached spells.
 * Enforces Frostgrave spell selection rules:
 * - Max 8 spells total
 * - 3 from Own school
 * - 3 from Aligned schools (combined)
 * - 2 from Neutral schools (combined)
 * - 0 from Opposed school (cannot learn)
 */

import { useState, useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Badge,
  Input,
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui';
import { Plus, Sparkles, Trash2, Search, BookOpen, Zap, X, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { cn } from '@/utils';
import {
  useWarbandStore,
  selectKnownSpells,
  selectWizard,
} from '@/store/warbandSlice';
import {
  SPELLS,
  getSpellsByIds,
  getSpellById,
  getEffectiveCastingNumber,
  getCategoryColor,
  getSchoolColor,
  SCHOOL_RELATIONSHIPS,
  type Spell,
  type WizardSchool,
} from '@/data/spellData';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_OWN_SCHOOL_SPELLS = 3;
const MAX_ALIGNED_SPELLS = 3;
const MAX_NEUTRAL_SPELLS = 2;
const MAX_TOTAL_SPELLS = 8; // 3 + 3 + 2

type SpellSourceCategory = 'own' | 'aligned' | 'neutral' | 'opposed';

// ---------------------------------------------------------------------------
// Helper Functions
// ---------------------------------------------------------------------------

function getSpellSourceCategory(
  spellSchool: WizardSchool,
  wizardSchool: WizardSchool
): SpellSourceCategory {
  if (spellSchool === wizardSchool) return 'own';

  const relationships = SCHOOL_RELATIONSHIPS[wizardSchool];
  if (relationships.aligned.includes(spellSchool)) return 'aligned';
  if (spellSchool === relationships.opposed) return 'opposed';
  return 'neutral';
}

function getCategoryLabel(category: SpellSourceCategory): string {
  switch (category) {
    case 'own':
      return 'Own School';
    case 'aligned':
      return 'Aligned';
    case 'neutral':
      return 'Neutral';
    case 'opposed':
      return 'Opposed';
  }
}

function getCategoryLimit(category: SpellSourceCategory): number {
  switch (category) {
    case 'own':
      return MAX_OWN_SCHOOL_SPELLS;
    case 'aligned':
      return MAX_ALIGNED_SPELLS;
    case 'neutral':
      return MAX_NEUTRAL_SPELLS;
    case 'opposed':
      return 0;
  }
}

// ---------------------------------------------------------------------------
// Spell Card Component (Full - for attached spells)
// ---------------------------------------------------------------------------

interface SpellCardProps {
  spell: Spell;
  wizardSchool?: WizardSchool | undefined;
  sourceCategory?: SpellSourceCategory;
  onRemove?: () => void;
  defaultExpanded?: boolean;
}

function SpellCard({ spell, wizardSchool, sourceCategory, onRemove, defaultExpanded = false }: SpellCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const wizardCN = wizardSchool
    ? getEffectiveCastingNumber(spell, wizardSchool, false)
    : spell.castingNumber;
  const apprenticeCN = wizardSchool
    ? getEffectiveCastingNumber(spell, wizardSchool, true)
    : spell.castingNumber + 2;

  return (
    <div
      className={cn(
        'relative rounded-lg border-2 border-border bg-gradient-to-br from-card to-card/80',
        'transition-all hover:border-primary/50'
      )}
    >
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-start justify-between gap-2 p-3 text-left"
      >
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-bold text-foreground">{spell.name}</h4>
            <Badge variant="outline" className={cn('text-[10px]', getSchoolColor(spell.school))}>
              {spell.school}
            </Badge>
            {sourceCategory && (
              <Badge
                variant="outline"
                className={cn(
                  'text-[9px]',
                  sourceCategory === 'own' && 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
                  sourceCategory === 'aligned' && 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                  sourceCategory === 'neutral' && 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                )}
              >
                {getCategoryLabel(sourceCategory)}
              </Badge>
            )}
          </div>
          <div className="mt-1 flex items-center gap-3 text-sm">
            <span className="flex items-center gap-1">
              <Zap className="h-3 w-3 text-primary" />
              <span className="font-semibold text-primary">CN {wizardCN}</span>
            </span>
            <span className="text-muted-foreground">
              Apprentice: {apprenticeCN}
            </span>
            <Badge variant="outline" className={cn('text-[9px]', getCategoryColor(spell.category))}>
              {spell.category}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {onRemove && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              aria-label={`Remove ${spell.name}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Description - Expandable */}
      {isExpanded && (
        <div className="border-t border-border bg-muted/30 px-3 py-2">
          <p className="text-sm leading-relaxed text-muted-foreground">
            <span className="font-medium text-foreground">Effect: </span>
            {spell.description}
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spell Picker Item (Compact - for spell library)
// ---------------------------------------------------------------------------

interface SpellPickerItemProps {
  spell: Spell;
  wizardSchool?: WizardSchool | undefined;
  onAdd: () => void;
  onViewDetails: () => void;
  disabled?: boolean;
  disabledReason?: string | undefined;
}

function SpellPickerItem({ spell, wizardSchool, onAdd, onViewDetails, disabled, disabledReason }: SpellPickerItemProps) {
  const wizardCN = wizardSchool
    ? getEffectiveCastingNumber(spell, wizardSchool, false)
    : spell.castingNumber;

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 rounded-lg border border-border',
        'bg-card p-3 transition-all',
        disabled ? 'opacity-60' : 'hover:border-primary/50'
      )}
    >
      <button
        onClick={onViewDetails}
        className="flex flex-1 flex-col gap-1 text-left"
      >
        <div className="flex items-center gap-2">
          <span className={cn('font-semibold', disabled && 'text-muted-foreground')}>{spell.name}</span>
          <span className={cn('text-sm font-bold', disabled ? 'text-muted-foreground' : 'text-primary')}>CN {wizardCN}</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={cn('text-[9px]', getSchoolColor(spell.school))}>
            {spell.school}
          </Badge>
          <Badge variant="outline" className={cn('text-[9px]', getCategoryColor(spell.category))}>
            {spell.category}
          </Badge>
        </div>
        {disabled && disabledReason && (
          <span className="text-xs text-destructive">{disabledReason}</span>
        )}
      </button>
      <Button
        size="sm"
        onClick={onAdd}
        className="h-8 shrink-0"
        disabled={disabled}
      >
        <Plus className="mr-1 h-3 w-3" />
        Add
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spell Detail Sheet
// ---------------------------------------------------------------------------

interface SpellDetailSheetProps {
  spell: Spell | null;
  wizardSchool?: WizardSchool | undefined;
  isOpen: boolean;
  onClose: () => void;
  onAdd?: (() => void) | undefined;
  showAddButton?: boolean;
  addDisabled?: boolean;
  addDisabledReason?: string | undefined;
}

function SpellDetailSheet({
  spell,
  wizardSchool,
  isOpen,
  onClose,
  onAdd,
  showAddButton,
  addDisabled,
  addDisabledReason,
}: SpellDetailSheetProps) {
  if (!spell) return null;

  const wizardCN = wizardSchool
    ? getEffectiveCastingNumber(spell, wizardSchool, false)
    : spell.castingNumber;
  const apprenticeCN = wizardSchool
    ? getEffectiveCastingNumber(spell, wizardSchool, true)
    : spell.castingNumber + 2;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-auto">
        <SheetHeader className="text-left">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <SheetTitle>{spell.name}</SheetTitle>
          </div>
          <SheetDescription className="sr-only">Spell details for {spell.name}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Badges */}
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className={cn('text-xs', getSchoolColor(spell.school))}>
              {spell.school}
            </Badge>
            <Badge variant="outline" className={cn('text-xs', getCategoryColor(spell.category))}>
              {spell.category}
            </Badge>
          </div>

          {/* Casting Numbers */}
          <div className="rounded-lg bg-muted/50 p-4">
            <h5 className="mb-2 text-sm font-medium text-muted-foreground">Casting Number</h5>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-md bg-primary/10 p-3 text-center">
                <div className="text-2xl font-bold text-primary">{wizardCN}</div>
                <div className="text-xs text-muted-foreground">Wizard</div>
              </div>
              <div className="rounded-md bg-muted p-3 text-center">
                <div className="text-2xl font-bold text-muted-foreground">{apprenticeCN}</div>
                <div className="text-xs text-muted-foreground">Apprentice (+2)</div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <h5 className="mb-2 text-sm font-medium text-muted-foreground">Effect</h5>
            <p className="text-sm leading-relaxed">{spell.description}</p>
          </div>

          {/* Add Button */}
          {showAddButton && onAdd && (
            <div className="space-y-2">
              <Button onClick={onAdd} className="w-full" disabled={addDisabled}>
                <Plus className="mr-2 h-4 w-4" />
                Add to Spellbook
              </Button>
              {addDisabled && addDisabledReason && (
                <p className="text-center text-xs text-destructive">{addDisabledReason}</p>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Tab Button Component
// ---------------------------------------------------------------------------

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  max: number;
  colorClass: string;
}

function TabButton({ active, onClick, label, count, max, colorClass }: TabButtonProps) {
  const isFull = count >= max;

  return (
    <button
      onClick={onClick}
      className={cn(
        'flex flex-col items-center rounded-lg px-3 py-2 text-center transition-all',
        'border-2',
        active
          ? cn('border-primary bg-primary/10', colorClass)
          : 'border-border bg-card hover:border-primary/50',
        isFull && !active && 'opacity-60'
      )}
    >
      <span className={cn('text-xs font-medium', active ? 'text-primary' : 'text-muted-foreground')}>
        {label}
      </span>
      <span className={cn('text-lg font-bold', isFull ? 'text-muted-foreground' : 'text-foreground')}>
        {count}/{max}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function SpellList() {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<SpellSourceCategory>('own');
  const [detailSpell, setDetailSpell] = useState<Spell | null>(null);
  const [detailMode, setDetailMode] = useState<'view' | 'add'>('view');

  // Store
  const knownSpells = useWarbandStore(selectKnownSpells);
  const wizard = useWarbandStore(selectWizard);
  const attachSpell = useWarbandStore((state) => state.attachSpell);
  const detachSpell = useWarbandStore((state) => state.detachSpell);

  const wizardSchool = wizard.school as WizardSchool | undefined;

  // Get spell data for attached spells with their source categories
  const attachedSpellsWithCategory = useMemo(() => {
    return getSpellsByIds(knownSpells).map((spell) => ({
      spell,
      sourceCategory: wizardSchool
        ? getSpellSourceCategory(spell.school, wizardSchool)
        : ('neutral' as SpellSourceCategory),
    }));
  }, [knownSpells, wizardSchool]);

  // Count spells by category
  const spellCounts = useMemo(() => {
    const counts = { own: 0, aligned: 0, neutral: 0, opposed: 0 };
    attachedSpellsWithCategory.forEach(({ sourceCategory }) => {
      counts[sourceCategory]++;
    });
    return counts;
  }, [attachedSpellsWithCategory]);

  // Get available spells for the active tab
  const availableSpells = useMemo(() => {
    if (!wizardSchool) return [];

    return SPELLS.filter((spell) => {
      // Not already attached
      if (knownSpells.includes(spell.id)) return false;

      // Must match active tab category
      const category = getSpellSourceCategory(spell.school, wizardSchool);
      if (category !== activeTab) return false;

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          spell.name.toLowerCase().includes(query) ||
          spell.school.toLowerCase().includes(query) ||
          spell.description.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [knownSpells, wizardSchool, activeTab, searchQuery]);

  // Check if can add more spells to current category
  const canAddToCategory = (category: SpellSourceCategory): boolean => {
    return spellCounts[category] < getCategoryLimit(category);
  };

  // Get reason why adding is disabled
  const getAddDisabledReason = (spell: Spell): string | undefined => {
    if (!wizardSchool) return 'Select a wizard school first';

    const category = getSpellSourceCategory(spell.school, wizardSchool);

    if (category === 'opposed') {
      return 'Cannot learn spells from opposed school';
    }

    if (!canAddToCategory(category)) {
      return `${getCategoryLabel(category)} slots full (${getCategoryLimit(category)}/${getCategoryLimit(category)})`;
    }

    if (knownSpells.length >= MAX_TOTAL_SPELLS) {
      return 'Spellbook full (8/8 spells)';
    }

    return undefined;
  };

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleAttach = (spellId: string) => {
    const spell = getSpellById(spellId);
    if (!spell || !wizardSchool) return;

    const disabledReason = getAddDisabledReason(spell);
    if (disabledReason) return;

    const result = attachSpell(spellId);
    if (result.success) {
      setDetailSpell(null);
    }
  };

  const handleDetach = (spellId: string) => {
    detachSpell(spellId);
  };

  const openSpellDetail = (spell: Spell, mode: 'view' | 'add') => {
    setDetailSpell(spell);
    setDetailMode(mode);
  };

  const closeSpellDetail = () => {
    setDetailSpell(null);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const relationships = wizardSchool ? SCHOOL_RELATIONSHIPS[wizardSchool] : null;

  return (
    <>
      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <CardTitle>Spellbook</CardTitle>
            </div>
            <Badge variant="outline" className="bg-primary/10">
              {knownSpells.length}/{MAX_TOTAL_SPELLS} spells
            </Badge>
          </div>
          <CardDescription>
            {wizardSchool ? (
              <>
                {wizardSchool} wizard • 3 own, 3 aligned, 2 neutral
              </>
            ) : (
              'Select a wizard school to manage spells'
            )}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Spell Slot Summary */}
          {wizardSchool && (
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg bg-emerald-500/10 p-2 text-center">
                <div className="text-xs text-emerald-400">Own</div>
                <div className="text-lg font-bold text-emerald-400">
                  {spellCounts.own}/{MAX_OWN_SCHOOL_SPELLS}
                </div>
              </div>
              <div className="rounded-lg bg-blue-500/10 p-2 text-center">
                <div className="text-xs text-blue-400">Aligned</div>
                <div className="text-lg font-bold text-blue-400">
                  {spellCounts.aligned}/{MAX_ALIGNED_SPELLS}
                </div>
              </div>
              <div className="rounded-lg bg-amber-500/10 p-2 text-center">
                <div className="text-xs text-amber-400">Neutral</div>
                <div className="text-lg font-bold text-amber-400">
                  {spellCounts.neutral}/{MAX_NEUTRAL_SPELLS}
                </div>
              </div>
            </div>
          )}

          {/* No School Warning */}
          {!wizardSchool && (
            <div className="flex items-center gap-2 rounded-lg bg-amber-500/10 p-3 text-amber-400">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="text-sm">Set your wizard's school above to manage spells</span>
            </div>
          )}

          {/* Spell List */}
          {attachedSpellsWithCategory.length > 0 ? (
            <div className="grid gap-3">
              {attachedSpellsWithCategory.map(({ spell, sourceCategory }) => (
                <SpellCard
                  key={spell.id}
                  spell={spell}
                  wizardSchool={wizardSchool}
                  sourceCategory={sourceCategory}
                  onRemove={() => handleDetach(spell.id)}
                />
              ))}
            </div>
          ) : wizardSchool ? (
            <div className="rounded-lg border border-dashed border-border bg-muted/30 py-8 text-center">
              <Sparkles className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                No spells in your spellbook yet.
              </p>
              <p className="text-xs text-muted-foreground">
                Add up to 8 spells (3 own + 3 aligned + 2 neutral)
              </p>
            </div>
          ) : null}

          {/* Add Spell Button */}
          {wizardSchool && (
            <Sheet open={isPickerOpen} onOpenChange={setIsPickerOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={knownSpells.length >= MAX_TOTAL_SPELLS}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {knownSpells.length >= MAX_TOTAL_SPELLS ? 'Spellbook Full' : 'Add Spell'}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="flex h-[85vh] flex-col">
                <SheetHeader className="shrink-0">
                  <SheetTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    Spell Library
                  </SheetTitle>
                  <SheetDescription>
                    Select spells by category • {knownSpells.length}/{MAX_TOTAL_SPELLS} slots used
                  </SheetDescription>
                </SheetHeader>

                {/* Category Tabs */}
                <div className="mt-4 grid shrink-0 grid-cols-3 gap-2">
                  <TabButton
                    active={activeTab === 'own'}
                    onClick={() => setActiveTab('own')}
                    label={wizardSchool}
                    count={spellCounts.own}
                    max={MAX_OWN_SCHOOL_SPELLS}
                    colorClass="text-emerald-400"
                  />
                  <TabButton
                    active={activeTab === 'aligned'}
                    onClick={() => setActiveTab('aligned')}
                    label="Aligned"
                    count={spellCounts.aligned}
                    max={MAX_ALIGNED_SPELLS}
                    colorClass="text-blue-400"
                  />
                  <TabButton
                    active={activeTab === 'neutral'}
                    onClick={() => setActiveTab('neutral')}
                    label="Neutral"
                    count={spellCounts.neutral}
                    max={MAX_NEUTRAL_SPELLS}
                    colorClass="text-amber-400"
                  />
                </div>

                {/* School Info */}
                {relationships && (
                  <div className="mt-2 shrink-0 text-xs text-muted-foreground">
                    {activeTab === 'own' && (
                      <span>Your school: {wizardSchool} (CN +0)</span>
                    )}
                    {activeTab === 'aligned' && (
                      <span>Aligned: {relationships.aligned.join(', ')} (CN +2)</span>
                    )}
                    {activeTab === 'neutral' && (
                      <span>Neutral: {relationships.neutral.join(', ')} (CN +4)</span>
                    )}
                  </div>
                )}

                {/* Search */}
                <div className="relative mt-3 shrink-0">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search spells..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
                      onClick={() => setSearchQuery('')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Category Full Warning */}
                {!canAddToCategory(activeTab) && (
                  <div className="mt-3 flex shrink-0 items-center gap-2 rounded-lg bg-amber-500/10 p-2 text-amber-400">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span className="text-xs">
                      {getCategoryLabel(activeTab)} slots full ({getCategoryLimit(activeTab)}/{getCategoryLimit(activeTab)})
                    </span>
                  </div>
                )}

                {/* Spell List - Scrollable */}
                <div className="mt-3 min-h-0 flex-1 overflow-y-auto pb-4">
                  <div className="space-y-2">
                    {availableSpells.length > 0 ? (
                      availableSpells.map((spell) => {
                        const disabledReason = getAddDisabledReason(spell);
                        return (
                          <SpellPickerItem
                            key={spell.id}
                            spell={spell}
                            wizardSchool={wizardSchool}
                            onAdd={() => handleAttach(spell.id)}
                            onViewDetails={() => openSpellDetail(spell, 'add')}
                            disabled={!!disabledReason}
                            disabledReason={disabledReason}
                          />
                        );
                      })
                    ) : (
                      <div className="py-8 text-center">
                        <p className="text-sm text-muted-foreground">
                          {searchQuery
                            ? 'No spells match your search'
                            : `All ${getCategoryLabel(activeTab).toLowerCase()} spells have been added`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          )}
        </CardContent>
      </Card>

      {/* Spell Detail Sheet */}
      <SpellDetailSheet
        spell={detailSpell}
        wizardSchool={wizardSchool}
        isOpen={!!detailSpell}
        onClose={closeSpellDetail}
        onAdd={
          detailMode === 'add' && detailSpell
            ? () => handleAttach(detailSpell.id)
            : undefined
        }
        showAddButton={detailMode === 'add'}
        addDisabled={detailSpell ? !!getAddDisabledReason(detailSpell) : false}
        addDisabledReason={detailSpell ? getAddDisabledReason(detailSpell) : undefined}
      />
    </>
  );
}
